import { GetServerSidePropsContext } from "next";
import { SWRConfig } from "swr";

import Bugsnag from "@bugsnag/js";
import getUnixTime from "date-fns/getUnixTime";
import startOfDay from "date-fns/startOfDay";

import EntityPageContainer from "components/EntityPageLayout";
import { entityLayoutData } from "components/EntityPageLayout/entityLayoutData";
import EntityPageAnnouncements from "components/EntityPageLayout/EntityPageAnnoucements";
import { entityEventsUrl } from "components/EntityPageLayout/EntityPageAnnoucements/EntityPageEventsList";
import { entityFilingsUrl } from "components/EntityPageLayout/EntityPageAnnoucements/EntityPageFilingsList";
import { entityPresentationsUrl } from "components/EntityPageLayout/EntityPageAnnoucements/EntityPagePresentationsList";
import { eventUrl } from "components/EventModal";
import { filingUrl } from "components/FilingModal";
import { presentationUrl } from "components/PresentationModal";
import type { NextPageWithLayout } from "pages/_app";
import { baseFetcher } from "utils/baseFetcher";
import { isSsr } from "utils/isSsr";

const fetcher = async (url: string, context: GetServerSidePropsContext) => {
  if (url.includes("events")) {
    const modifiedUrl =
      url + `&current-time=${getUnixTime(startOfDay(new Date()))}`;
    return baseFetcher(modifiedUrl, context);
  }

  return baseFetcher(url, context);
};

const EntityAnnouncementsTabPage: NextPageWithLayout = () => {
  return <EntityPageAnnouncements />;
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const slug = context.params?.slug as string;
    const tab = context.query.tab as string;
    const itemId = context.query.item as string;

    const announcementsUrl =
      tab === "presentations"
        ? entityPresentationsUrl(slug, 1)
        : tab === "events"
        ? entityEventsUrl(slug, 1)
        : entityFilingsUrl(slug, 1);
    const announcementUrl =
      tab === "presentations"
        ? presentationUrl(itemId)
        : tab === "events"
        ? eventUrl(itemId)
        : filingUrl(itemId);

    const [{ entity, entityLayoutFallback }, announcements, announcement] =
      await Promise.all([
        entityLayoutData(slug, context),
        isSsr(context) && !itemId // fix empty list after close item modal
          ? fetcher(announcementsUrl, context)
          : Promise.resolve(null),
        isSsr(context) && itemId
          ? fetcher(announcementUrl, context)
          : Promise.resolve({}),
      ]);

    if (!entity || entity.errors?.length) return { notFound: true };

    return {
      props: {
        slug,
        fallback: {
          ...entityLayoutFallback,
          [`$inf$${announcementsUrl}`]: [announcements],
          [announcementUrl]: announcement,
        },
      },
    };
  } catch (error: any) {
    console.error("[slug].tsx ~ getServerSideProps ~ error", error);
    Bugsnag.notify(error);
    return { notFound: true };
  }
}

EntityAnnouncementsTabPage.getLayout = (page, { pageProps }) => {
  const { fallback } = pageProps;

  return (
    <SWRConfig value={{ fallback }}>
      <EntityPageContainer {...pageProps}>{page}</EntityPageContainer>
    </SWRConfig>
  );
};

export default EntityAnnouncementsTabPage;
